import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';

const initTimelineCarousels = () => {
    const blockContainers = document.querySelectorAll('.timeline-carousel-block');

    if (!blockContainers.length) return;

    blockContainers.forEach((block) => {
        const carouselContainer = block.querySelector('.timeline-swiper');
        const slideElements = block.querySelectorAll('.timeline-card');
        const nextBtns = block.querySelectorAll('.card-next');
        const prevBtns = block.querySelectorAll('.card-prev');

        if (!carouselContainer || !slideElements.length) return;

        /**
         * DOM Preparation
         */
        slideElements.forEach((slide, index) => {
            if (!slide.getAttribute('data-slide-index')) {
                slide.setAttribute('data-slide-index', index);
            }
            if (slide.querySelector('.card-progress-bar')) return;

            const cardYear = slide.querySelector('.card-year');
            const cardBody = slide.querySelector('.card-body');

            if (cardYear && cardBody) {
                const progressBar = document.createElement('div');
                progressBar.className = 'card-progress-bar';
                progressBar.innerHTML = '<div class="progress-fill"></div><div class="dot"></div>';
                cardYear.insertAdjacentElement('afterend', progressBar);
            }
        });

        /**
         * Swiper Initialization
         */
        if (!block.dataset.config) return;
        const swiperConfig = JSON.parse(block.dataset.config);
        
        const swiper = new Swiper(carouselContainer, {
            modules: [Navigation, Pagination, Autoplay],
            ...swiperConfig,
            watchSlidesProgress: true,
            // We REMOVE the navigation: {} object from here to use our custom handlers
        });

        /**
         * State Management (The Recommended Way)
         * Updates the disabled classes based on swiper state and progress logic.
         */
        const updateNavButtons = () => {
            // Check if the second slide (index 1) or any further slide is filled
            const isAnyProgressMade = Array.from(slideElements)
                .slice(1) // Skip the first slide because it's filled by default
                .some(slide => slide.classList.contains('is-filled'));

            // Prev Button: Disabled ONLY if we are at the very first slide AND no progress bars beyond the first are filled
            prevBtns.forEach(btn => {
                const shouldDisablePrev = swiper.isBeginning && !isAnyProgressMade;
                btn.classList.toggle('swiper-button-disabled', shouldDisablePrev);
            });

            // Next Button: Disabled if we are at the end AND the last visible progress bar is filled
            const lastSlide = slideElements[slideElements.length - 1];
            const isLastProgressFilled = lastSlide.classList.contains('is-filled');
            
            nextBtns.forEach(btn => {
                btn.classList.toggle('swiper-button-disabled', swiper.isEnd && isLastProgressFilled);
            });
        };

        const syncSlideProgress = () => {
            const allSlides = block.querySelectorAll('.swiper-slide');
            allSlides.forEach((el) => {
                const index = el.getAttribute('data-slide-index') || el.dataset.swiperSlideIndex;
                const sourceSlide = block.querySelector(
                    `.swiper-slide:not(.swiper-slide-duplicate)[data-slide-index="${index}"]`
                );
                if (sourceSlide) {
                    const isFilled = sourceSlide.classList.contains('is-filled');
                    el.classList.toggle('is-filled', isFilled);
                }
            });
            updateNavButtons(); // Check button state whenever progress changes
        };

        /**
         * Navigation Logic
         */
        const handleStepNext = () => {
            const total = slideElements.length;
            const perView = Math.min(total, Math.ceil(swiper.params.slidesPerView));
            const startIdx = swiper.realIndex;
            let wasStepFilled = false;

            for (let i = 0; i < perView; i++) {
                const targetIdx = startIdx + i;
                if (targetIdx >= total) continue;

                const slide = block.querySelector(
                    `.swiper-slide:not(.swiper-slide-duplicate)[data-slide-index="${targetIdx}"]`
                );

                if (slide && !slide.classList.contains('is-filled')) {
                    slide.classList.add('is-filled');
                    wasStepFilled = true;
                    break;
                }
            }

            if (!wasStepFilled && !swiper.isEnd) {
                swiper.slideNext();
            } else {
                syncSlideProgress();
            }
        };

        const handleStepPrev = () => {
            const total = slideElements.length;
            const perView = Math.min(total, Math.ceil(swiper.params.slidesPerView));
            const startIdx = swiper.realIndex;
            let wasStepRemoved = false;

            for (let i = perView - 1; i >= 0; i--) {
                const targetIdx = startIdx + i;
                if (targetIdx >= total || i === 0) continue;

                const slide = block.querySelector(
                    `.swiper-slide:not(.swiper-slide-duplicate)[data-slide-index="${targetIdx}"]`
                );

                if (slide && slide.classList.contains('is-filled')) {
                    slide.classList.remove('is-filled');
                    wasStepRemoved = true;
                    break;
                }
            }

            if (!wasStepRemoved && !swiper.isBeginning) {
                swiper.slidePrev();
            } else {
                syncSlideProgress();
            }
        };

        /**
         * Event Binding
         */
        nextBtns.forEach((btn) => btn.addEventListener('click', handleStepNext));
        prevBtns.forEach((btn) => btn.addEventListener('click', handleStepPrev));

        swiper.on('slideChange', () => {
            const activeIdx = swiper.realIndex;
            const mainSlides = block.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');

            mainSlides.forEach((slide) => {
                const slideIdx = parseInt(slide.getAttribute('data-slide-index'), 10);
                slide.classList.toggle('is-filled', slideIdx <= activeIdx);
            });
            syncSlideProgress();
        });

        /**
         * Initialization State
         */
        const firstSlide = block.querySelector(
            '.swiper-slide:not(.swiper-slide-duplicate)[data-slide-index="0"]'
        );
        if (firstSlide) {
            firstSlide.classList.add('is-filled');
            syncSlideProgress();
        }
        
        // Final check on load
        updateNavButtons();
    });
};

document.addEventListener('DOMContentLoaded', initTimelineCarousels);